import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Configuration } from 'src/app/models/configurators-2d/applications/carpentry/configuration';
import { QuotationRecord } from 'src/app/models/configurators-2d/applications/carpentry/quotation-record';
import { ShoppingCartDiscount } from 'src/app/models/shopping-cart/shopping-cart-discount';
import { ShoppingCartProduct } from 'src/app/models/shopping-cart/shopping-cart-product';
import { ShoppingCartProductCategory } from 'src/app/models/shopping-cart/shopping-cart-product-category';
import { ShoppingCartProductList } from 'src/app/models/shopping-cart/shopping-cart-product-list';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { ShoppingCartElementEnum } from 'src/app/enum/shopping-cart/shopping-cart-element.enum';
import { CarpentryService } from 'src/app/services/configurators-2d/applications/carpentry.service';
import { CustomerService } from 'src/app/services/customer.service';
import { ModalService, MODAL } from 'src/app/services/modal.service';
import { PrintService } from 'src/app/services/print.service';
import { cloneDeep } from 'src/app/services/helpers/utils';
import { TunnelDataStore } from 'src/app/stores/tunnel/tunnel-data.store';
import { TunnelNavigationStore } from 'src/app/stores/tunnel/tunnel-navigation.store';
import { Price } from 'src/app/stores/tunnel/model/price';
import { CarpentryOrder } from 'src/app/models/configurators-2d/applications/carpentry/carpentry-order';
import { hasCarpentrySupplierSendingError } from 'src/app/services/helpers/carpentry-order-display.helper';
import {
  CarpentryProductListSortDirection,
  CarpentryProductListSortField,
  CarpentryProductListSortDirections,
  getDefaultCarpentryProductListSortDirections,
} from 'src/app/models/configurators-2d/applications/carpentry/carpentry-product-list-sort.model';

@Injectable({
  providedIn: 'root',
})
export class CarpentryTunnelStore {
  private readonly service = inject(CarpentryService);
  private readonly modalService = inject(ModalService);
  private readonly printService = inject(PrintService);
  private readonly customerService = inject(CustomerService);
  private readonly tunnelDataStore = inject(TunnelDataStore);
  private readonly tunnelNavigationStore = inject(TunnelNavigationStore);
  private readonly translateService = inject(TranslateService);

  // État partagé
  products: QuotationRecord[] = [];
  private defaultOrderedProducts: QuotationRecord[] = [];
  private configurationId?: string;
  totalAmount = 0;
  discountOptionsMap: Record<string, { label: string; value: number }[]> = {};
  loader = false;
  discounts: ShoppingCartDiscount[] | null = null;
  sortDirections: CarpentryProductListSortDirections = getDefaultCarpentryProductListSortDirections();

  orderEditionLoading = false;

  readonly tvaOptions = [
    { label: '5,5%', value: 5.5 },
    { label: '10%', value: 10 },
    { label: '20%', value: 20 },
  ];

  get order(): CarpentryOrder | undefined {
    return this.service.configuration?.orders?.sort((a, b) => new Date(b.sendDate).getTime() - new Date(a.sendDate).getTime())[0];
  }

  // ── Navigation ────────────────────────────────────────────────────────────

  addProduct(): void {
    this.tunnelNavigationStore.navigateToStep(0);
  }

  editRecord(record: QuotationRecord): void {
    this.service.pendingEditQuotationRecordId = record.externalId;
    this.tunnelNavigationStore.navigateToStep(0);
  }

  /** Appelé depuis subscribeToObservable() à chaque émission du tunnelDataStore. */
  setConfiguration(configuration: Configuration): void {
    if (this.configurationId !== configuration.id) {
      this.sortDirections = getDefaultCarpentryProductListSortDirections();
      this.configurationId = configuration.id;
    }

    this.defaultOrderedProducts = this.getDefaultOrderedProducts(configuration.quotationRecords || []);
    this.defaultOrderedProducts.forEach(record => {
      if (record.informations) {
        record.informations.sort((a,b) => (a.order ?? 0) - (b.order ?? 0));
      }
    });
    this.applySorting();
    this.applyDiscountsToProducts();
    this.computeDiscountOptions();
    this.computeTotalAmount();
  }

  /** Charge les remises disponibles depuis le backend et recalcule les données produits. */
  async loadDiscounts(): Promise<void> {
    this.discounts = await this.service.getProductDiscounts();
    this.applyDiscountsToProducts();
    this.computeDiscountOptions();
    this.computeTotalAmount();
  }

  // ── Gestion des remises ────────────────────────────────────────────────────

  onDiscountChange(record: QuotationRecord, discountValue: number): void {
    const selectedDiscount = this.discounts?.find(
      d => d.productCode === record.productCode && d.discountValue === discountValue
    );
    if (selectedDiscount) {
      record.ean = selectedDiscount.ean;
      this.syncToServiceConfiguration(record);
      this.computeTotalAmount();
      this.service.configuration.configurationHasChanged = true;
      this.tunnelDataStore.resetCurrentFunding();
    }
  }

  // ── Gestion de la TVA ─────────────────────────────────────────────────────

  async onTvaChange(record: QuotationRecord): Promise<void> {
    const newExternalId = await this.service.updateTva(record as unknown as ShoppingCartProduct);
    if (newExternalId != null) {
      this.updateExternalId(record, newExternalId);
      const serviceRecord = this.service.configuration.quotationRecords.find(r => r.externalId === newExternalId);
      if (serviceRecord) {
        serviceRecord.tva = record.tva;
      }
      this.service.configuration.configurationHasChanged = true;
      this.computeTotalAmount();
      this.tunnelDataStore.resetCurrentFunding();
    }
  }

  // ── Gestion des quantités ─────────────────────────────────────────────────

  async onQuantityChange(record: QuotationRecord): Promise<void> {
    const newExternalId = await this.service.updateQuantity(record as unknown as ShoppingCartProduct);
    if (newExternalId != null) {
      this.updateExternalId(record, newExternalId);
      const serviceRecord = this.service.configuration.quotationRecords.find(r => r.externalId === newExternalId);
      if (serviceRecord) {
        serviceRecord.quantity = record.quantity;
      }
      this.service.configuration.configurationHasChanged = true;
      this.computeTotalAmount();
      this.tunnelDataStore.resetCurrentFunding();
    }
  }

  // ── Actions CRUD ──────────────────────────────────────────────────────────

  async copyRecord(record: QuotationRecord): Promise<void> {
    this.loader = true;
    try {
      const cloned = cloneDeep<QuotationRecord>(record);
      const newExternalId = await this.service.duplicate(cloned);
      if (newExternalId == null) return;

      const details = await this.service.getQuotationRecordDetails(newExternalId);
      const product = this.service.convertDetailToShoppingCartProduct(details);
      const newRecord = this.service.convertToQuotationRecord(product);
      newRecord.accessories = record.accessories ? cloneDeep(record.accessories) : [];
      newRecord.discount = record.discount;
      newRecord.tva = record.tva;
      newRecord.index = cloned.index;
      newRecord.externalId = newExternalId;

      const originalIndex = this.service.configuration.quotationRecords.findIndex(
        r => r.externalId === record.externalId
      );
      if (originalIndex !== -1) {
        this.service.configuration.quotationRecords.splice(originalIndex + 1, 0, newRecord);
      } else {
        this.service.configuration.quotationRecords.push(newRecord);
      }

      this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.CARPENTRY, this.service.configuration);
      this.tunnelDataStore.resetCurrentFunding();
    } finally {
      this.loader = false;
    }
  }

  deleteRecord(record: QuotationRecord): void {
    this.modalService.display(MODAL.SHOPPING_CART_PRODUCT_DELETE, {
      element: ShoppingCartElementEnum.Product,
      onClose: async (deleted: boolean) => {
        this.modalService.close();
        if (deleted) {
          this.service.configuration.quotationRecords = this.service.configuration.quotationRecords.filter(
            r => r.externalId !== record.externalId
          );
          this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.CARPENTRY, this.service.configuration);
          this.tunnelDataStore.resetCurrentFunding();
          await this.service.saveProject();
        }
      },
    });
  }

  // Fait évoluer l'état de tri de la colonne sélectionnée.
  toggleSort(field: CarpentryProductListSortField): void {
    const currentDirection = this.sortDirections[field];
    if (currentDirection === CarpentryProductListSortDirection.None) {
      this.sortDirections[field] = CarpentryProductListSortDirection.Asc;
    } else if (currentDirection === CarpentryProductListSortDirection.Asc) {
      this.sortDirections[field] = CarpentryProductListSortDirection.Desc;
    } else {
      this.sortDirections[field] = CarpentryProductListSortDirection.None;
    }
    this.applySorting();
  }

  // Rétablit l'ordre par défaut.
  resetSort(): void {
    this.sortDirections = getDefaultCarpentryProductListSortDirections();
    this.applySorting();
  }

  // ── Méthodes privées ──────────────────────────────────────────────────────

  // Reconstruit la liste visible selon les tris actifs.
  private applySorting(): void {
    if (!this.defaultOrderedProducts?.length) {
      this.products = [];
      return;
    }

    const sortedProducts = [...this.defaultOrderedProducts];
    const activeFields = this.getActiveSortFields();
    const defaultOrderMap = new Map(this.defaultOrderedProducts.map((record, index) => [record.externalId, index]));

    if (activeFields.length > 0) {
      sortedProducts.sort((left, right) => {
        for (const field of activeFields) {
          const direction = this.sortDirections[field] === CarpentryProductListSortDirection.Asc ? 1 : -1;
          const leftValue = this.getSortableValue(left, field);
          const rightValue = this.getSortableValue(right, field);
          const comparison = leftValue.localeCompare(rightValue, 'fr', { sensitivity: 'base' }) * direction;

          if (comparison !== 0) {
            return comparison;
          }
        }

        return (defaultOrderMap.get(left.externalId) ?? 0) - (defaultOrderMap.get(right.externalId) ?? 0);
      });
    }

    this.products = sortedProducts;
  }

  // Reconstitue l'ordre par defaut a partir de l'index metier persiste.
  private getDefaultOrderedProducts(products: QuotationRecord[]): QuotationRecord[] {
    return products
      .map((record, position) => ({ record, position }))
      .sort((left, right) => {
        const leftIndex = left.record.index ?? Number.MAX_SAFE_INTEGER;
        const rightIndex = right.record.index ?? Number.MAX_SAFE_INTEGER;

        if (leftIndex !== rightIndex) {
          return leftIndex - rightIndex;
        }

        return left.position - right.position;
      })
      .map(item => item.record);
  }

  // Retourne les champs actuellement utilisés pour trier.
  private getActiveSortFields(): CarpentryProductListSortField[] {
    // Priorité de tri voulue : fournisseur puis désignation quand les deux sont actifs.
    return [CarpentryProductListSortField.SupplierName, CarpentryProductListSortField.Name].filter(
      field => this.sortDirections[field] !== CarpentryProductListSortDirection.None
    );
  }

  // Extrait la valeur utilisée pour trier le champ demandé.
  private getSortableValue(record: QuotationRecord, field: CarpentryProductListSortField): string {
    if (field === CarpentryProductListSortField.SupplierName) {
      return record.supplierName ?? '';
    }

    return record.name ?? '';
  }

  private isDiscountExpired(discount: ShoppingCartDiscount): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(discount.validityEndDate) < today;
  }

  private applyDiscountsToProducts(): void {
    if (!this.discounts || !this.products?.length) return;

    this.products.forEach(record => {
      const validDiscounts = this.discounts.filter(
        d => d.productCode === record.productCode && !this.isDiscountExpired(d)
      );
      if (!validDiscounts?.length) return;

      let selectedDiscount: ShoppingCartDiscount;

      const currentIsExpiredOrMissing =
        record.discount == null || !validDiscounts.some(d => d.discountValue === record.discount);

      if (currentIsExpiredOrMissing) {
        selectedDiscount =
          validDiscounts.find(d => d.isDefault) ??
          validDiscounts.toSorted((a, b) => a.discountValue - b.discountValue)[0];
      } else {
        selectedDiscount = validDiscounts.find(d => d.discountValue === record.discount);
      }

      if (selectedDiscount) {
        record.discount = selectedDiscount.discountValue;
        record.ean = selectedDiscount.ean;
      }
    });
  }

  private computeDiscountOptions(): void {
    if (!this.discounts) return;
    this.products.forEach(record => {
      const validDiscounts = this.discounts.filter(
        d => d.productCode === record.productCode && !this.isDiscountExpired(d)
      );
      this.discountOptionsMap[record.externalId] = validDiscounts
        .toSorted((a, b) => a.discountValue - b.discountValue)
        .map(d => ({ label: `${d.discountValue}%`, value: d.discountValue }));
    });
  }

  private computeTotalAmount(): void {
    this.totalAmount = this.products.reduce((sum, record) => sum + record.totalPriceDiscountTtc, 0);
    this.updatePricingInStore();
  }

  private updatePricingInStore(): void {
    const total = new Price({ baseAmount: this.totalAmount, actualAmount: this.totalAmount });
    this.tunnelDataStore.updateCurrentPricing({
      totalConception: total,
      totalWithoutFunding: total,
    });
  }

  private updateExternalId(record: QuotationRecord, newExternalId: number): void {
    const oldExternalId = record.externalId;
    const serviceRecord = this.service.configuration.quotationRecords.find(r => r.externalId === oldExternalId);
    if (serviceRecord) {
      serviceRecord.externalId = newExternalId;
    }
    record.externalId = newExternalId;

    // Migrer la clé du discountOptionsMap : le template utilise record.externalId comme clé,
    // donc si l'id change, les options de remise deviendraient introuvables sans cette migration.
    if (this.discountOptionsMap[oldExternalId] !== undefined) {
      this.discountOptionsMap[newExternalId] = this.discountOptionsMap[oldExternalId];
      delete this.discountOptionsMap[oldExternalId];
    }
  }

  // ── Édition numéro de commande ────────────────────────────────────────────

  // Prépare les quotation records pour la modale d'édition du numéro de commande.
  async openOrderNumberEdition(): Promise<void> {
    this.orderEditionLoading = true;
    const records = this.service.configuration.quotationRecords ?? [];
    const shoppingCartProducts: ShoppingCartProduct[] = records.map(r => this.service.convertToShoppingCartProduct(r));
    const category = new ShoppingCartProductCategory('Menuiseries', undefined, shoppingCartProducts);
    const productList = new ShoppingCartProductList([category]);

    await this.service.prepareOrderNumberEdition(productList);
    this.service.recreateQuotationRecords();
    this.orderEditionLoading = false;

    const orders = this.service.configuration.orders;
    const order = orders?.length
      ? orders.toSorted((a, b) => new Date(a.sendDate).getTime() - new Date(b.sendDate).getTime()).at(-1)
      : undefined;

    this.modalService.display(MODAL.SHOPPING_CART_ORDER_NUMBER_EDITION, {
      productList,
      service: this.service,
      order,
      onClose: async (updatedProductList: ShoppingCartProductList) => {
        this.modalService.close();
        if (updatedProductList) {
          await this.service.getProjectById(this.service.configuration.id, true);
          this.service.recreateQuotationRecords();
          this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.CARPENTRY, this.service.configuration);
        }
      },
    });
  }

  // ── Impression ────────────────────────────────────────────────────────────

  printList(): void {
    this.printService.reset();
    this.printService.addHeader("Liste d'achat — Menuiserie").then(() => {
      if (this.customerService.isLogged) {
        this.printService.addText('Client n° ' + this.customerService.userDatas.id, 'my-3');
        this.printService.addText('Nom : ' + this.customerService.fullName, 'my-3');
      }

      const now = new Date();
      const dateStr = [
        String(now.getDate()).padStart(2, '0'),
        String(now.getMonth() + 1).padStart(2, '0'),
        now.getFullYear(),
      ].join('/');
      this.printService.addText('Le ' + dateStr, 'mt-3 mb-5');

      const totalDiv = document.createElement('div');
      totalDiv.style.cssText = 'text-align:right;margin-bottom:20px;font-size:16px;font-weight:bold;';
      totalDiv.textContent = `Total TTC : ${this.formatAmount(this.totalAmount)}`;
      this.printService.addDiv(totalDiv);

      this.printService.addTable(this.buildPrintTable()).then(() => {
        this.printService.print();
      });
    });
  }

  private formatAmount(amount: number): string {
    return amount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  }

  private buildPrintTable(): HTMLTableElement {
    const cell = 'border:1px solid #ddd;padding:8px;font-size:12px;vertical-align:top;';
    const header = cell + 'background:#f5f5f5;font-weight:bold;';

    const table = document.createElement('table');
    table.style.cssText = 'width:100%;border-collapse:collapse;';

    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    ['EAN', 'Fournisseur', 'Désignation', 'Remise', 'TVA', 'Prix unit. TTC', 'Qté', 'Total TTC', 'Réf. Elcia', 'Statut'].forEach(text => {
      const th = document.createElement('th');
      th.style.cssText = header;
      th.textContent = text;
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    this.products.forEach(record => {
      const tr = document.createElement('tr');
      const mainCells: { html?: string; text?: string; style?: string }[] = [
        { text: record.ean },
        { text: record.supplierName },
        { text: record.name },
        { text: record.discount > 0 ? `${record.discount}%` : '—' },
        { text: `${record.tva}%` },
        { html: this.formatPriceCell(record.unitPriceTtc, record.unitPriceDiscountTtc, record.discount) },
        { text: String(record.quantity) },
        { html: this.formatPriceCell(record.totalPriceTtc, record.totalPriceDiscountTtc, record.discount) },
        this.buildPrintedReferenceElciaCell(record),
        { text: record.supplierSent ? this.translateService.instant('popins.products_list.main.columns.supplier-sent-status') : this.translateService.instant('popins.products_list.main.columns.supplier-pending-status') },
      ];
      mainCells.forEach(c => {
        const td = document.createElement('td');
        td.style.cssText = cell + (c.style ?? '');
        if (c.html != null) {
          td.innerHTML = c.html;
        } else {
          td.textContent = c.text ?? '';
        }
        tr.appendChild(td);
      });
      tbody.appendChild(tr);

      const expansionTr = document.createElement('tr');
      const expansionTd = document.createElement('td');
      expansionTd.colSpan = 10;
      expansionTd.style.cssText = cell + 'background:#fafafa;';

      const content = document.createElement('div');
      content.style.cssText = 'display:flex;gap:16px;align-items:flex-start;';

      if (record.thumbnail) {
        const img = document.createElement('img');
        img.src = record.thumbnail;
        img.style.cssText = 'width:80px;height:80px;object-fit:contain;flex-shrink:0;';
        content.appendChild(img);
      }

      const infos = document.createElement('div');
      if (record.informations?.length) {
        record.informations.forEach(info => {
          const p = document.createElement('p');
          p.style.cssText = 'margin:2px 0;font-size:11px;color:#444;';
          p.textContent = info.label;
          infos.appendChild(p);
        });
      } else {
        const p = document.createElement('p');
        p.style.cssText = 'font-size:12px;color:#999;font-style:italic;';
        p.textContent = 'Aucune information disponible';
        infos.appendChild(p);
      }
      content.appendChild(infos);

      expansionTd.appendChild(content);
      expansionTr.appendChild(expansionTd);
      tbody.appendChild(expansionTr);
    });
    table.appendChild(tbody);

    return table;
  }

  private formatPriceCell(original: number, discounted: number, discount: number): string {
    if (discount > 0) {
      return `<span style="text-decoration:line-through;color:#bbb;margin-right:4px;">${this.formatAmount(original)}</span>${this.formatAmount(discounted)}`;
    }
    return this.formatAmount(discounted);
  }

  private buildPrintedReferenceElciaCell(record: QuotationRecord): { text: string; style?: string } {
    if (hasCarpentrySupplierSendingError(this.order, record)) {
      return {
        text: this.translateService.instant('popins.products_list.main.columns.supplier-sending-error'),
        style: 'color:#d32f2f;font-weight:700;white-space:nowrap;',
      };
    }

    return {
      text: record.referenceElcia ?? '',
      style: 'white-space:nowrap;',
    };
  }

  private syncToServiceConfiguration(record: QuotationRecord): void {
    const serviceRecord = this.service.configuration.quotationRecords.find(r => r.externalId === record.externalId);
    if (serviceRecord) {
      serviceRecord.discount = record.discount;
      serviceRecord.ean = record.ean;
    }
    this.service.configuration.configurationHasChanged = true;
  }
}
